import { useCallback, useEffect, useRef, useState } from 'react'
import { Box, TextField, ToggleButton, ToggleButtonGroup, Typography, Button, ButtonGroup } from '@mui/material'
import { ChevronLeft, ChevronRight } from '@mui/icons-material'
import { StaticDateRangePicker, LocalizationProvider, CalendarPickerSkeleton, DatePicker } from '@mui/lab'
import { isMobile, useMobileOrientation } from 'react-device-detect'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { getData, setFeatureFlag } from '../../api/dataSlice'
import { isDashboardDataLoaded, getDashboardMinDate, getDashboardMaxDate, loadDashboardData, getPresetRanges, rangeOverlaps } from '../Utilities'
import { useKeycloak } from '../Keycloak'
import { Accordion, AccordionSummary, AccordionDetails } from '../controls/Accordion'
import { LenientDayjsAdapter } from '../Utilities'
import i18next from 'i18next'
import dayjs from 'dayjs'
import CameraSelection from '../controls/CameraSelection'

// convert DD.MM.YYYY or DD.MM.YY to YYYY-MM-DD so dayjs can parse it
function parseDateString(s) {
    const m = s && s.match(/^(\d{1,2})\.(\d{1,2})\.(\d{2,4})$/)
    if (m) {
        let y = parseInt(m[3])
        if (y < 100) y += 2000
        return `${y}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`
    }
    return s
}

// clamp a day to the [min, max] data bounds (values may be strings or dayjs); returns a start-of-day dayjs
function clampDay(day, minVal, maxVal) {
    let d = day.startOf('day')
    const lo = minVal ? dayjs(minVal).startOf('day') : null
    const hi = maxVal ? dayjs(maxVal).startOf('day') : null
    if (lo && d.isBefore(lo)) d = lo
    if (hi && d.isAfter(hi)) d = hi
    return d
}

// gated by MYSITE route access (role check skipped on `protected: false`)
export default function DashboardAccordion({ expanded, setExpanded, onMysiteViewChange, isPortrait, loading: parentLoading, timestamp, onDashboardStateChange, dashboardState }) {

    const { t } = useTranslation()
    const { hasRouteAccess, isLoggedIn } = useKeycloak()
    const { isPortrait: mobilePortrait } = useMobileOrientation()
    const { CAMERAS, BASEURL, ROUTES: { MYSITE } } = window.conf
    const { siderWidth } = useSelector(getData)

    const dispatch = useDispatch()
    const mountedRef = useRef(true)
    const onStateChangeRef = useRef(onDashboardStateChange)

    onStateChangeRef.current = onDashboardStateChange

    const hasDashboard = hasRouteAccess(MYSITE)
    const today = dayjs().startOf('day')
    const moreThanOneCam = CAMERAS.length > 1
    const daySize = 34
    const initialDate = timestamp ? dayjs(timestamp) : null // use the shared timestamp from Redux as the initial date
    const isMobilePortrait = isMobile && mobilePortrait
    const presetRanges = getPresetRanges(today)

    const [selectedCamera, setSelectedCamera] = useState(dashboardState?.selectedCamera ?? CAMERAS?.[0]?.id) // dashboardState (Content) first -> survives mobile-portrait footer unmount
    const [dateRange, setDateRange] = useState(dashboardState?.dateRange ?? (initialDate ? [initialDate, initialDate] : [today, today]))
    const [predefinedRange, setPredefinedRange] = useState('')
    const [forceRefresh, setForceRefresh] = useState(true)
    const [dataLoading, setDataLoading] = useState(true)
    const [dataMinDate, setDataMinDate] = useState(null)
    const [dataMaxDate, setDataMaxDate] = useState(null)

    // drill-down history (depth 1): back target lives in the parent dashboardState
    const previousRangeRef = useRef(null)
    const dateRangeRef = useRef(dateRange)
    const predefinedRangeRef = useRef(predefinedRange)
    dateRangeRef.current = dateRange
    predefinedRangeRef.current = predefinedRange

    const activeTab = dashboardState?.activeTab ?? 'detections'
    const dateRangeKind = activeTab === 'weather' ? 'weather' : 'detections'
    const minDate = dataMinDate ? dayjs(dataMinDate) : null
    const maxDate = dataMaxDate ? dayjs(dataMaxDate) : null
    const noData = !minDate || !maxDate
    const disableControls = parentLoading || dataLoading
    const disableDateControls = disableControls || noData
    
    // single-day range -> show the prev/next day stepper (drill-down, initial load, "yesterday" preset, …)
    const drillDay = dateRange?.[0]
    const isSingleDay = !!(drillDay && dateRange?.[1]) && drillDay.isSame(dateRange[1], 'day')
    const canStepPrev = !!drillDay && (!minDate || drillDay.isAfter(minDate, 'day'))
    const canStepNext = !!drillDay && (!maxDate || drillDay.isBefore(maxDate, 'day'))

    useEffect(() => {
        dispatch(setFeatureFlag({ name: 'hasDashboard', value: !!hasDashboard }))
    }, [dispatch, hasDashboard])

    // preload dashboard RAG data once the feature becomes available (body reads from cache on mount)
    useEffect(() => {
        if (!hasDashboard) return

        const preload = async () => {
            const camId = CAMERAS?.[0]?.id ?? 0
            const dataUrl = `${BASEURL}cam${camId + 1}/rag_data/`
            await loadDashboardData(dataUrl, camId)

            // seed an initial dashboardState if the body hasn't pushed one yet (mobile portrait closed-footer case)
            if (isDashboardDataLoaded(camId) && !dashboardState?.dataReady) {
                const min = getDashboardMinDate(camId, 'detections')
                const max = getDashboardMaxDate(camId, 'detections')
                const today = dayjs().startOf('day')
                const dMin = min ? dayjs(min) : today.subtract(1, 'year')
                const dMax = max ? dayjs(max) : today

                // stubs that update dashboardState directly — needed when the body is unmounted
                // (mobile portrait with closed footer); overridden with real handlers once it mounts
                const drillDownStub = (dateString) => {
                    const drillDate = dayjs(dateString)
                    if (drillDate.isValid()) {
                        onDashboardStateChange(prev => prev ? {
                            ...prev,
                            dateRange: [drillDate, drillDate],
                            previousRange: prev.canGoBack ? prev.previousRange : { dateRange: prev.dateRange, predefinedRange: '' },
                            canGoBack: true
                        } : prev)
                    }
                }

                const drillUpStub = () => {
                    onDashboardStateChange(prev => {
                        if (!prev) return prev
                        if (!prev.previousRange) return { ...prev, canGoBack: false, previousRange: null }
                        return { ...prev, dateRange: prev.previousRange.dateRange, canGoBack: false, previousRange: null }
                    })
                }

                const stepDayStub = (delta) => {
                    onDashboardStateChange(prev => {
                        if (!prev?.dateRange?.[0]) return prev
                        const d = clampDay(dayjs(prev.dateRange[0]).add(delta, 'day'), prev.minDate, prev.maxDate)
                        return { ...prev, dateRange: [d, d] }
                    })
                }

                const setDateRangeStub = (startString, endString) => {
                    const start = dayjs(startString)
                    const end = dayjs(endString)
                    if (start.isValid() && end.isValid()) {
                        onDashboardStateChange(prev => prev ? { ...prev, dateRange: [start, end] } : prev)
                    }
                }

                const setSelectedCameraStub = (id) => {
                    if (id != null) {
                        onDashboardStateChange(prev => prev ? { ...prev, selectedCamera: id } : prev)
                    }
                }

                onDashboardStateChange({
                    selectedCamera: camId,
                    dateRange: [dMax, dMax],
                    dataReady: true,
                    minDate: dMin,
                    maxDate: dMax,
                    canGoBack: false,
                    previousRange: null,
                    onDrillDown: drillDownStub,
                    onDrillUp: drillUpStub,
                    onStepDay: stepDayStub,
                    onSetDateRange: setDateRangeStub,
                    onSetSelectedCamera: setSelectedCameraStub
                })
            }
        }
        preload()
    // intentionally only runs when `hasDashboard` flips to true — including dashboardState/onDashboardStateChange (would re-preload on every dashboard-state update)
    }, [hasDashboard]) // eslint-disable-line react-hooks/exhaustive-deps

    // any explicit range change (calendar, presets, camera, chatbot setDates) drops the drill-down "back" target; canGoBack/previousRange are cleared in the parent so the body's button hides even when this accordion is unmounted
    const clearPreviousRange = useCallback(() => {
        previousRangeRef.current = null
        onStateChangeRef.current?.(prev => (prev && (prev.canGoBack || prev.previousRange)) ? { ...prev, canGoBack: false, previousRange: null } : prev)
    }, [])

    // stable drill-down handler — works even as a stale closure after this accordion unmounts (mobile portrait, footer closed): local setState is guarded by mountedRef, parent state flows through the stable setDashboardState
    const handleDrillDown = useCallback((dateString) => {
        const drillDate = dayjs(parseDateString(dateString))
        if (!drillDate.isValid()) return
        const presetForBack = mountedRef.current ? predefinedRangeRef.current : '' // preserve the preset highlight on "back" (desktop/landscape)
        if (mountedRef.current) {
            if (!previousRangeRef.current) previousRangeRef.current = { dateRange: dateRangeRef.current, predefinedRange: predefinedRangeRef.current }
            setDateRange([drillDate, drillDate])
            setPredefinedRange('')
            setForceRefresh(prev => !prev)
        }
        // push once: keep the original overview as the back target across stepping / re-drilling (prev.canGoBack guards it)
        onStateChangeRef.current?.(prev => prev ? {
            ...prev,
            dateRange: [drillDate, drillDate],
            previousRange: prev.canGoBack ? prev.previousRange : { dateRange: prev.dateRange, predefinedRange: presetForBack },
            canGoBack: true
        } : prev)
    }, [])

    // general date range setter (used by the chatbot's setDates action)
    const handleSetDateRange = useCallback((startString, endString) => {
        const start = dayjs(parseDateString(startString))
        const end = dayjs(parseDateString(endString))
        if (!start.isValid() || !end.isValid()) return
        clearPreviousRange()
        if (mountedRef.current) {
            setDateRange([start, end])
            setPredefinedRange('')
            setForceRefresh(prev => !prev)
        }
        onStateChangeRef.current?.(prev => prev ? { ...prev, dateRange: [start, end] } : prev)
    }, [clearPreviousRange])

    // camera setter (used by the chatbot's setSelectedCameraId action)
    const handleSetSelectedCamera = useCallback((id) => {
        if (id == null) return
        clearPreviousRange() // new camera scope — old overview no longer applies
        if (mountedRef.current) {
            setSelectedCamera(id)
        }
        onStateChangeRef.current?.(prev => prev ? { ...prev, selectedCamera: id } : prev)
    }, [clearPreviousRange])

    // restore the pre-drill-down range ("Back to overview"); parent state is authoritative (works unmounted), local restore is best-effort for the pickers
    const handleDrillUp = useCallback(() => {
        if (mountedRef.current && previousRangeRef.current) {
            const restored = previousRangeRef.current
            setDateRange(restored.dateRange)
            setPredefinedRange(restored.predefinedRange || '')
            setForceRefresh(prev => !prev)
        }
        previousRangeRef.current = null
        onStateChangeRef.current?.(prev => {
            if (!prev) return prev
            if (!prev.previousRange) return { ...prev, canGoBack: false, previousRange: null }
            return { ...prev, dateRange: prev.previousRange.dateRange, canGoBack: false, previousRange: null }
        })
    }, [])

    // step the single-day window to an adjacent day without dropping the "back" target (bounded by data min/max)
    const handleStepDay = useCallback((delta) => {
        if (mountedRef.current) {
            const cur = dateRangeRef.current?.[0]
            if (!cur) return
            const d = clampDay(dayjs(cur).add(delta, 'day'), dataMinDate, dataMaxDate)
            setDateRange([d, d])
            setPredefinedRange('') // stepping moves off any preset (e.g. "yesterday") -> drop the toggle highlight
            setForceRefresh(prev => !prev)
            onStateChangeRef.current?.(prev => prev ? { ...prev, dateRange: [d, d] } : prev)
        } else {
            // unmounted (portrait, footer closed): compute from the authoritative parent state
            onStateChangeRef.current?.(prev => {
                if (!prev?.dateRange?.[0]) return prev
                const d = clampDay(dayjs(prev.dateRange[0]).add(delta, 'day'), prev.minDate, prev.maxDate)
                return { ...prev, dateRange: [d, d] }
            })
        }
    }, [dataMinDate, dataMaxDate])

    useEffect(() => () => { mountedRef.current = false }, [])

    // load dashboard data for the currently selected camera and adjust initial dateRange; re-runs on tab switch
    useEffect(() => {
        if (selectedCamera == null) return

        const applyLoadedData = () => {
            const min = getDashboardMinDate(selectedCamera, dateRangeKind)
            const max = getDashboardMaxDate(selectedCamera, dateRangeKind)
            setDataMinDate(min)
            setDataMaxDate(max)

            // choose a sensible initial range if the current one doesn't overlap with the data
            if (min && max) {
                const dMin = dayjs(min)
                const dMax = dayjs(max)
                const needsFallback = !rangeOverlaps(dateRange[0], dateRange[1], dMin, dMax)

                if (needsFallback) {
                    clearPreviousRange() // scope changed enough to remap the range — the old overview no longer applies
                    const [dayStart, dayEnd] = presetRanges.day
                    const [weekStart, weekEnd] = presetRanges.week
                    const [monthStart, monthEnd] = presetRanges.month

                    if (rangeOverlaps(today, today, dMin, dMax)) {
                        setDateRange([today, today])
                        setPredefinedRange('')
                    } else if (rangeOverlaps(dayStart, dayEnd, dMin, dMax)) {
                        setDateRange([dayStart, dayEnd])
                        setPredefinedRange('day')
                    } else if (rangeOverlaps(weekStart, weekEnd, dMin, dMax)) {
                        setDateRange([weekStart, weekEnd])
                        setPredefinedRange('week')
                    } else if (rangeOverlaps(monthStart, monthEnd, dMin, dMax)) {
                        setDateRange([monthStart, monthEnd])
                        setPredefinedRange('month')
                    } else {
                        setDateRange([dMin, dMax])
                        setPredefinedRange('total')
                    }
                    setForceRefresh(prev => !prev)
                }
            }

            setDataLoading(false)
        }

        if (isDashboardDataLoaded(selectedCamera)) {
            applyLoadedData()
        } else {
            setDataLoading(true)
            const dataUrl = `${BASEURL}cam${selectedCamera + 1}/rag_data/`
            loadDashboardData(dataUrl, selectedCamera).then(() => {
                if (!mountedRef.current) return
                applyLoadedData()
            })
        }
    }, [selectedCamera, dateRangeKind]) // eslint-disable-line react-hooks/exhaustive-deps

    // notify parent on actual state changes (not on callback-identity changes); functional update preserves fields the body owns (e.g. `activeTab`)
    useEffect(() => {
        onStateChangeRef.current?.(prev => ({
            ...(prev || {}),
            selectedCamera,
            dateRange,
            dataReady: !dataLoading && isDashboardDataLoaded(selectedCamera),
            onDrillDown: handleDrillDown,
            onDrillUp: handleDrillUp,
            onStepDay: handleStepDay,
            onSetDateRange: handleSetDateRange,
            onSetSelectedCamera: handleSetSelectedCamera,
            minDate,
            maxDate
        }))
    }, [selectedCamera, dateRange, dataLoading, handleDrillDown, handleDrillUp, handleStepDay, handleSetDateRange, handleSetSelectedCamera, dataMinDate, dataMaxDate]) // eslint-disable-line react-hooks/exhaustive-deps

    const onChangeDateRange = (newRange) => {
        if (newRange?.length) {
            clearPreviousRange()
            setDateRange([newRange[0], newRange[1]])
            setPredefinedRange('')
        }
    }

    const onChangeStartDate = (startDate) => {
        if (startDate?.isValid?.()) {
            clearPreviousRange()
            setDateRange([startDate, dateRange[1]])
            setPredefinedRange('')
        }
    }

    const onChangeEndDate = (endDate) => {
        if (endDate?.isValid?.()) {
            clearPreviousRange()
            setDateRange([dateRange[0], endDate])
            setPredefinedRange('')
        }
    }

    const onChangePredefinedSpan = (_, newSpan) => {
        if (!newSpan) return
        clearPreviousRange()
        if (newSpan === 'total') {
            setDateRange([minDate, maxDate])
        } else if (presetRanges[newSpan]) {
            setDateRange(presetRanges[newSpan])
        }
        setPredefinedRange(newSpan)
        setForceRefresh(prev => !prev)
    }

    const onChangeCamera = (id) => {
        clearPreviousRange() // new camera scope — old overview no longer applies
        setSelectedCamera(id)
    }

    return (
        <Accordion
            expanded={expanded === 'dashboard'}
            onChange={(event, newExpanded) => {
                setExpanded(newExpanded ? 'dashboard' : false)
                onMysiteViewChange(newExpanded ? 'dashboard' : null)
            }}
            isPortrait={isPortrait}
            disabled={!hasDashboard}
            disabledTooltip={!isLoggedIn() ? t('msg.sign_in_to_unlock') : undefined}
        >
            <AccordionSummary className="accordion-with-subtitle">
                <Box>
                    <Typography>{t('pages.dashboard.title')}</Typography>
                    <Typography className="accordion-subtitle">{t('pages.dashboard.subtitle')}</Typography>
                </Box>
            </AccordionSummary>
            <AccordionDetails>
                <Box>
                    {moreThanOneCam && (
                        <CameraSelection
                            value={selectedCamera}
                            onChange={onChangeCamera}
                            disabled={disableControls}
                        />
                    )}

                    <LocalizationProvider dateAdapter={LenientDayjsAdapter} locale={i18next.language}>
                        <Box sx={{ display: 'flex' }}>
                            <DatePicker
                                label={t('date.start_date')}
                                inputFormat={t('date.format.short')}
                                mask={t('date.mask.short')}
                                disableMaskedInput
                                renderInput={props =>
                                    <TextField
                                        {...props}
                                        sx={{
                                            width: '100%',
                                            userSelect: 'none',
                                            '& .MuiOutlinedInput-root': { '& > fieldset': { borderColor: '#4caf50' } },
                                            '& .MuiInputAdornment-root': { margin: '-4px' }
                                        }}
                                    />
                                }
                                value={dateRange[0]}
                                onChange={onChangeStartDate}
                                cancelText={t('actions.cancel')}
                                toolbarTitle={t('date.select_date')}
                                minDate={minDate}
                                maxDate={dateRange[1]}
                                loading={dataLoading}
                                disabled={disableDateControls}
                                renderLoading={() => <CalendarPickerSkeleton />}
                                showTodayButton
                                todayText={t("date.today")}
                            />

                            <p style={{
                                paddingLeft: 12,
                                paddingRight: 12,
                                height: '100%',
                                alignItems: 'center',
                                userSelect: 'none'
                            }}>
                                {t('date.to')}
                            </p>

                            <DatePicker
                                label={t('date.end_date')}
                                inputFormat={t('date.format.short')}
                                mask={t('date.mask.short')}
                                disableMaskedInput
                                renderInput={props =>
                                    <TextField
                                        {...props}
                                        sx={{
                                            width: '100%',
                                            userSelect: 'none',
                                            '& .MuiOutlinedInput-root': { '& > fieldset': { borderColor: '#ed6c02' } },
                                            '& .MuiInputAdornment-root': { margin: '-4px' }
                                        }}
                                    />
                                }
                                value={dateRange[1]}
                                onChange={onChangeEndDate}
                                cancelText={t('actions.cancel')}
                                toolbarTitle={t('date.select_date')}
                                minDate={dateRange[0]}
                                maxDate={maxDate}
                                loading={dataLoading}
                                disabled={disableDateControls}
                                renderLoading={() => <CalendarPickerSkeleton />}
                                showTodayButton
                                todayText={t("date.today")}
                            />
                        </Box>
                    </LocalizationProvider>

                    {/* single-day range: prev / next day button group (the "‹ date ›" variant is portrait-footer only) */}
                    <ButtonGroup
                        fullWidth
                        variant="outlined"
                        disabled={disableDateControls || !isSingleDay}
                        sx={{ mt: 2 }}
                    >
                        <Button
                            startIcon={<ChevronLeft />}
                            onClick={() => handleStepDay(-1)}
                            disabled={!canStepPrev}
                            sx={{ textTransform: 'none' }}
                        >
                            {t('date.previous_day')}
                        </Button>
                        <Button
                            endIcon={<ChevronRight />}
                            onClick={() => handleStepDay(1)}
                            disabled={!canStepNext}
                            sx={{ textTransform: 'none' }}
                        >
                            {t('date.next_day')}
                        </Button>
                    </ButtonGroup>

                    {!isMobile && (
                        <Box
                            sx={{
                                '& > div': { minWidth: siderWidth, pt: 2 },
                                '& > div > div': {
                                    border: '1px solid',
                                    borderColor: '#BDBDBD',
                                    borderRadius: '4px'
                                },
                                '& > div > div, & > div > div > div, & > div > div > div > div': { width: siderWidth - 2 },
                                '& .MuiTypography-caption': { width: daySize + 4, margin: 0 },
                                '& .PrivatePickersSlideTransition-root': {
                                    minHeight: daySize * 7.2,
                                    overflow: 'hidden'
                                },
                                '& .PrivatePickersSlideTransition-root [role="grid"]': { width: siderWidth },
                                '& .MuiPickersDay-root': { width: daySize, height: daySize }
                            }}
                        >
                            <LocalizationProvider dateAdapter={LenientDayjsAdapter} locale={i18next.language} key={forceRefresh}>
                                <StaticDateRangePicker
                                    calendars={1}
                                    displayStaticWrapperAs="desktop"
                                    mask={t('date.mask.full')}
                                    value={dateRange}
                                    onChange={onChangeDateRange}
                                    renderInput={() => {}}
                                    minDate={minDate}
                                    maxDate={maxDate}
                                    loading={dataLoading}
                                    disabled={disableDateControls}
                                    renderLoading={() => <CalendarPickerSkeleton />}
                                />
                            </LocalizationProvider>
                        </Box>
                    )}

                    <ToggleButtonGroup
                        color="primary"
                        exclusive
                        onChange={onChangePredefinedSpan}
                        value={disableDateControls ? '' : predefinedRange} // suppress the "selected" highlight while the group is disabled — keeps the visual in lockstep with the date pickers (state itself stays intact for when controls re-enable)
                        fullWidth
                        size={isMobilePortrait ? "small" : "medium"}
                        orientation={isMobilePortrait ? "horizontal" : "vertical"}
                        sx={{ pt: 2 }}
                    >
                        <ToggleButton
                            disabled={disableDateControls || !rangeOverlaps(presetRanges.day[0], presetRanges.day[1], minDate, maxDate)}
                            value="day"
                        >
                            {t('date.yesterday')}
                        </ToggleButton>
                        <ToggleButton
                            disabled={disableDateControls || !rangeOverlaps(presetRanges.week[0], presetRanges.week[1], minDate, maxDate)}
                            value="week"
                        >
                            {t('date.last_week')}
                        </ToggleButton>
                        <ToggleButton
                            disabled={disableDateControls || !rangeOverlaps(presetRanges.month[0], presetRanges.month[1], minDate, maxDate)}
                            value="month"
                        >
                            {t('date.last_month')}
                        </ToggleButton>
                        {!isMobilePortrait && (
                            <ToggleButton disabled={disableDateControls} value="total">
                                {t('pages.dashboard.preset_total')}
                            </ToggleButton>
                        )}
                    </ToggleButtonGroup>
                </Box>
            </AccordionDetails>
        </Accordion>
    )
}
